import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RetoureListItem, RetoureStatus, RetoureGrund, Gutschrift, GutschriftStatus } from '../types';
import './RetoureList.css';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const STATUS_LABEL: Record<RetoureStatus, string> = {
  ANGEMELDET: 'Angemeldet',
  WARE_EMPFANGEN: 'Ware empfangen',
  IN_PRUEFUNG: 'In Prüfung',
  ABGESCHLOSSEN: 'Abgeschlossen',
  ABGELEHNT: 'Abgelehnt',
};

const STATUS_CLASS: Record<RetoureStatus, string> = {
  ANGEMELDET: 'rl-status-angemeldet',
  WARE_EMPFANGEN: 'rl-status-empfangen',
  IN_PRUEFUNG: 'rl-status-pruefung',
  ABGESCHLOSSEN: 'rl-status-abgeschlossen',
  ABGELEHNT: 'rl-status-abgelehnt',
};

const GRUND_LABEL: Record<RetoureGrund, string> = {
  FALSCHE_MENGE: 'Falsche Menge',
  FALSCHE_WARE: 'Falsche Ware',
  QUALITAETSMANGEL: 'Qualitätsmangel',
  BESCHAEDIGUNG: 'Beschädigung',
  LIEFERFEHLER: 'Lieferfehler',
  SONSTIGES: 'Sonstiges',
};

const GUTSCHRIFT_STATUS_LABEL: Record<GutschriftStatus, string> = {
  ENTWURF: 'Entwurf',
  VERSENDET: 'Versendet',
  VERRECHNET: 'Verrechnet',
  AUSGEZAHLT: 'Ausgezahlt',
};

type AktionTyp = 'empfang' | 'pruefung' | 'abschliessen' | 'ablehnen';

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE');
}

function formatEUR(value?: number | null): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return err.message;
  }
  return 'Unbekannter Fehler';
}

const RetoureList: React.FC = () => {
  const [retouren, setRetouren] = useState<RetoureListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');

  const [gutschriften, setGutschriften] = useState<Gutschrift[]>([]);
  const [gsLoading, setGsLoading] = useState(true);
  const [gsFehler, setGsFehler] = useState<string | null>(null);

  const [aktion, setAktion] = useState<{ id: number; typ: AktionTyp } | null>(null);
  const [ablehnungsgrund, setAblehnungsgrund] = useState('');
  const [aktionLaedt, setAktionLaedt] = useState(false);
  const [aktionFehler, setAktionFehler] = useState<string | null>(null);
  const [akteId, setAkteId] = useState<number | null>(null);
  const [gutschriftAkteId, setGutschriftAkteId] = useState<number | null>(null);

  const loadRetouren = useCallback(async () => {
    setLoading(true);
    setFehler(null);
    setAkteId(null);
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const res = await axios.get<RetoureListItem[]>(`${API_URL}/api/retouren`, { params });
      setRetouren(res.data);
    } catch (err) {
      setFehler(`Retouren konnten nicht geladen werden: ${extractError(err)}`);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const loadGutschriften = useCallback(async () => {
    setGsLoading(true);
    setGsFehler(null);
    try {
      const res = await axios.get<Gutschrift[]>(`${API_URL}/api/gutschriften`);
      setGutschriften(res.data);
    } catch (err) {
      setGsFehler(`Gutschriften konnten nicht geladen werden: ${extractError(err)}`);
    } finally {
      setGsLoading(false);
    }
  }, []);

  useEffect(() => { loadRetouren(); }, [loadRetouren]);
  useEffect(() => { loadGutschriften(); }, [loadGutschriften]);

  const openAktion = (id: number, typ: AktionTyp) => {
    setAktionFehler(null);
    setAblehnungsgrund('');
    setAktion({ id, typ });
  };

  const closeAktion = () => {
    setAktion(null);
    setAktionFehler(null);
    setAblehnungsgrund('');
  };

  const bestaetigenAktion = async () => {
    if (!aktion) return;
    const { id, typ } = aktion;
    if (typ === 'ablehnen' && !ablehnungsgrund.trim()) {
      setAktionFehler('Bitte einen Ablehnungsgrund angeben.');
      return;
    }
    setAktionLaedt(true);
    setAktionFehler(null);
    try {
      if (typ === 'empfang') {
        await axios.post(`${API_URL}/api/retouren/${id}/empfang`, { positionen: [] });
      } else if (typ === 'pruefung') {
        await axios.post(`${API_URL}/api/retouren/${id}/pruefung`, { positionen: [] });
      } else if (typ === 'abschliessen') {
        await axios.post(`${API_URL}/api/retouren/${id}/abschliessen`);
      } else if (typ === 'ablehnen') {
        await axios.post(`${API_URL}/api/retouren/${id}/ablehnen`, null, {
          params: { ablehnungsgrund },
        });
      }
      closeAktion();
      await Promise.all([loadRetouren(), loadGutschriften()]);
    } catch (err) {
      setAktionFehler(extractError(err));
    } finally {
      setAktionLaedt(false);
    }
  };

  const aktionLabel: Record<AktionTyp, string> = {
    empfang: 'Wareneingang buchen',
    pruefung: 'QS-Prüfung abschließen',
    abschliessen: 'Retoure abschließen (Gutschrift erstellen)',
    ablehnen: 'Retoure ablehnen',
  };

  return (
    <div className="retoure-list-container">
      <div className="list-header">
        <h2>Retouren</h2>
        <div className="header-actions">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Status</option>
            {(Object.keys(STATUS_LABEL) as RetoureStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : fehler ? (
        <div className="rl-error">
          <span>{fehler}</span>
          <button className="rl-retry-btn" onClick={loadRetouren}>Erneut versuchen</button>
        </div>
      ) : retouren.length === 0 ? (
        <div className="empty-state">
          <p>Keine Retouren vorhanden</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="retoure-table">
            <thead>
              <tr>
                <th>Retoure-Nr.</th>
                <th>Auftrag</th>
                <th>Kunde</th>
                <th>Grund</th>
                <th>Status</th>
                <th>Angemeldet</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {retouren.map(r => (
                <React.Fragment key={r.retoure_id}>
                  <tr>
                    <td className="retoure-nr">{r.retoure_nr}</td>
                    <td>Auftrag #{r.auftrag_id}</td>
                    <td>Kunde #{r.kunde_id}</td>
                    <td>{GRUND_LABEL[r.grund] || r.grund}</td>
                    <td>
                      <span className={`status-badge ${STATUS_CLASS[r.status] || ''}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td>{formatDate(r.angemeldet_am)}</td>
                    <td className="action-cell">
                      {r.status === 'ANGEMELDET' && (
                        <button className="btn-aktion" onClick={() => openAktion(r.retoure_id, 'empfang')}>
                          Wareneingang
                        </button>
                      )}
                      {r.status === 'WARE_EMPFANGEN' && (
                        <button className="btn-aktion" onClick={() => openAktion(r.retoure_id, 'pruefung')}>
                          QS-Prüfung
                        </button>
                      )}
                      {r.status === 'IN_PRUEFUNG' && (
                        <button className="btn-aktion" onClick={() => openAktion(r.retoure_id, 'abschliessen')}>
                          Abschließen
                        </button>
                      )}
                      {(r.status === 'ANGEMELDET' || r.status === 'WARE_EMPFANGEN' || r.status === 'IN_PRUEFUNG') && (
                        <button className="btn-aktion btn-aktion-danger" onClick={() => openAktion(r.retoure_id, 'ablehnen')}>
                          Ablehnen
                        </button>
                      )}
                      <button
                        className="btn-aktion"
                        style={{ marginLeft: 6 }}
                        onClick={() => setAkteId(akteId === r.retoure_id ? null : r.retoure_id)}
                      >
                        {akteId === r.retoure_id ? 'Akte schließen' : 'Akte'}
                      </button>
                    </td>
                  </tr>
                  {r.beschreibung && (
                    <tr className="rl-beschreibung-row">
                      <td colSpan={7}>
                        <span className="rl-beschreibung-label">Beschreibung:</span> {r.beschreibung}
                      </td>
                    </tr>
                  )}
                  {r.status === 'ABGELEHNT' && r.ablehnungsgrund && (
                    <tr className="rl-beschreibung-row">
                      <td colSpan={7}>
                        <span className="rl-beschreibung-label">Ablehnungsgrund:</span> {r.ablehnungsgrund}
                      </td>
                    </tr>
                  )}
                  {aktion && aktion.id === r.retoure_id && (
                    <tr className="rl-aktion-row">
                      <td colSpan={7}>
                        <div className="rl-aktion-confirm">
                          <span className="rl-aktion-frage">
                            {aktionLabel[aktion.typ]} für <strong>{r.retoure_nr}</strong> wirklich durchführen?
                          </span>
                          {aktion.typ === 'ablehnen' && (
                            <input
                              type="text"
                              className="rl-ablehnungsgrund-input"
                              placeholder="Ablehnungsgrund *"
                              value={ablehnungsgrund}
                              onChange={e => setAblehnungsgrund(e.target.value)}
                              autoFocus
                            />
                          )}
                          {aktionFehler && <div className="rl-inline-fehler">{aktionFehler}</div>}
                          <div className="rl-aktion-buttons">
                            <button
                              className="btn-aktion-confirm"
                              onClick={bestaetigenAktion}
                              disabled={aktionLaedt}
                            >
                              {aktionLaedt ? 'Wird ausgeführt...' : 'Bestätigen'}
                            </button>
                            <button className="btn-aktion-cancel" onClick={closeAktion}>
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {akteId === r.retoure_id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                        <AktivitaetenBlock entitaetTyp="retoure" entitaetId={r.retoure_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section className="rl-gutschriften-section">
        <h3>Gutschriften</h3>
        {gsLoading ? (
          <div className="loading">Lädt...</div>
        ) : gsFehler ? (
          <div className="rl-error">
            <span>{gsFehler}</span>
            <button className="rl-retry-btn" onClick={loadGutschriften}>Erneut versuchen</button>
          </div>
        ) : gutschriften.length === 0 ? (
          <div className="empty-state">
            <p>Keine Gutschriften vorhanden</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="retoure-table">
              <thead>
                <tr>
                  <th>Gutschrift-Nr.</th>
                  <th>Betrag (brutto)</th>
                  <th>Status</th>
                  <th>Erstellt</th>
                  <th>Versendet</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {gutschriften.map(g => (
                  <React.Fragment key={g.gutschrift_id}>
                    <tr>
                      <td className="retoure-nr">{g.gutschrift_nr}</td>
                      <td className="price-cell">{formatEUR(g.brutto_betrag)}</td>
                      <td>
                        <span className="status-badge">{GUTSCHRIFT_STATUS_LABEL[g.status] || g.status}</span>
                      </td>
                      <td>{formatDate(g.erstellt_am)}</td>
                      <td>{formatDate(g.versendet_am)}</td>
                      <td className="action-cell">
                        <button
                          className="btn-aktion"
                          onClick={() => setGutschriftAkteId(gutschriftAkteId === g.gutschrift_id ? null : g.gutschrift_id)}
                        >
                          {gutschriftAkteId === g.gutschrift_id ? 'Akte schließen' : 'Akte'}
                        </button>
                      </td>
                    </tr>
                    {gutschriftAkteId === g.gutschrift_id && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                          <AktivitaetenBlock entitaetTyp="gutschrift" entitaetId={g.gutschrift_id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default RetoureList;
